// Sync types (webdavsync-as-an-acequia-js-capability, phase 5).
//
// A type is a named preset: the options and patterns a kind of sync usually
// wants, and a POLICY the engine enforces for every caller. A config names
// one with `type`. Its options, exclusions and includes are defaults the
// config may override; its policy is a floor — a config may add
// restrictions (more destination-owned paths, never delete) but never take
// the type's away. That is what lets a type keep a promise: a Deploy leaves
// the destination's access files alone whoever runs it.
//
// The owner decision of 2026-09-24: `.acequia-access.json` is handled here,
// as destination-owned policy, not as a hard-coded default exclusion.

import { DEFAULT_EXCLUSIONS } from './patterns.js'

const ACCESS_FILE = '.acequia-access.json'

// What the documentation publisher takes from the tree (its allowlist;
// localWebDAV/bin/publish-docs.mjs since 2026-09-28, upload-docs.sh before).
const DOCUMENT_EXTENSIONS = ['md', 'json', 'png', 'jpg', 'jpeg', 'gif', 'svg', 'webp', 'pdf']

export const SYNC_TYPES = Object.freeze({
    deploy: {
        id: 'deploy',
        name: 'Deploy an app',
        description: 'A checkout to a server. The server keeps its access files; nothing is ever deleted.',
        options: { mirrorMode: false, skipLargeFiles: true, ignoreTimestamps: false, forceCopyAll: false },
        exclusionPatterns: [...DEFAULT_EXCLUSIONS, '*.local.*', 'test/'],
        policy: { neverDelete: true, destinationOwned: [ACCESS_FILE] },
    },
    'publish-docs': {
        id: 'publish-docs',
        name: 'Publish documentation',
        description: 'A documentation checkout to /documentation/: documents and images only, archive/ stays home, a moved document\'s old URL goes away (mirror within the allowlist), access files stay the server\'s.',
        // Mirror, since 2026-09-28 (owner decision, publish-the-documentation
        // project): the disk layout IS the published layout, so what the
        // checkout lacks the server should lack too — within the allowlist,
        // which bounds the deletes as it bounds the copies. The per-area
        // access sidecars are destination-owned: written once when an area
        // is added, never overwritten, never deleted, whatever mirror says.
        options: { mirrorMode: true, skipLargeFiles: true, ignoreTimestamps: false, forceCopyAll: false },
        exclusionPatterns: [...DEFAULT_EXCLUSIONS, 'archive/'],
        includePatterns: DOCUMENT_EXTENSIONS.map((ext) => `*.${ext}`),
        policy: { neverDelete: false, destinationOwned: [ACCESS_FILE] },
    },
    backup: {
        id: 'backup',
        name: 'Back up a folder',
        description: 'Copy what changed; nothing is ever deleted at the destination.',
        options: { mirrorMode: false, skipLargeFiles: false, ignoreTimestamps: false, forceCopyAll: false },
        exclusionPatterns: [...DEFAULT_EXCLUSIONS],
        policy: { neverDelete: true },
    },
    mirror: {
        id: 'mirror',
        name: 'Mirror',
        description: 'Make the destination exactly the source: copies, and deletes what the source lacks. The plan shows every delete first.',
        options: { mirrorMode: true, skipLargeFiles: false, ignoreTimestamps: false, forceCopyAll: false },
        exclusionPatterns: [...DEFAULT_EXCLUSIONS],
        policy: {},
    },
    'seed-fixture': {
        id: 'seed-fixture',
        name: 'Seed a test fixture',
        description: 'Production (read-only) onto a scratch copy on another host. Mirror is allowed — the destination is the scratch copy — and a destination on the source host is refused.',
        options: { mirrorMode: true, skipLargeFiles: false, ignoreTimestamps: true, forceCopyAll: false },
        exclusionPatterns: [],
        policy: { refuseDestinationOnSourceHost: true },
    },
})

export function listTypes() {
    return Object.values(SYNC_TYPES).map(({ id, name, description }) => ({ id, name, description }))
}

/**
 * Fold a type into a config: defaults under the config's values, policy as
 * the union of the type's and the config's. Returns the config unchanged when
 * it names no type; throws on an unknown one.
 */
export function applyType(config) {
    if (!config || !config.type) return config
    const type = SYNC_TYPES[config.type]
    if (!type) throw new Error(`sync: unknown sync type "${config.type}" (known: ${Object.keys(SYNC_TYPES).join(', ')})`)
    const policy = config.policy || {}
    return {
        ...config,
        options: { ...type.options, ...(config.options || {}) },
        exclusionPatterns: Array.isArray(config.exclusionPatterns) ? config.exclusionPatterns : [...type.exclusionPatterns],
        includePatterns: Array.isArray(config.includePatterns) && config.includePatterns.length
            ? config.includePatterns
            : [...(type.includePatterns || [])],
        policy: {
            neverDelete: !!(type.policy.neverDelete || policy.neverDelete),
            destinationOwned: [...new Set([...(type.policy.destinationOwned || []), ...(policy.destinationOwned || [])])],
            refuseDestinationOnSourceHost: !!(type.policy.refuseDestinationOnSourceHost || policy.refuseDestinationOnSourceHost),
        },
    }
}
